import React, { useState } from 'react';
import { Order, OrderStatus, Department, StaffMember, Vendor } from '../../lib/types';
import { Modal } from '../ui';
import { createOrder, updateOrderField } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { FormItem, emptyItem } from './types';

interface EditOrderModalProps {
  open: boolean;
  onClose: () => void;
  editingOrder: Order | null;
  departments: Department[];
  staffDirectory: StaffMember[];
  categories: string[];
  vendors: Vendor[];
  currency: string;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  onSaved: () => void;
  onDuplicate: (message: string, retry: () => void) => void;
}

export function EditOrderModal(props: EditOrderModalProps) {
  const {
    open, onClose, editingOrder, departments, staffDirectory,
    categories, vendors, currency, showToast, onSaved, onDuplicate,
  } = props;

  const isEdit = !!editingOrder;

  const [formDate,     setFormDate]     = useState(editingOrder?.order_date || new Date().toISOString().split('T')[0]);
  const [formStaff,    setFormStaff]    = useState(editingOrder?.staff_name || '');
  const [formDept,     setFormDept]     = useState(editingOrder?.department_id || '');
  const [formVendor,   setFormVendor]   = useState(editingOrder?.vendor || '');
  const [formCategory, setFormCategory] = useState(editingOrder?.category || '');
  const [formOrderNum, setFormOrderNum] = useState(editingOrder?.order_number || '');
  const [formStatus,   setFormStatus]   = useState<string>(editingOrder?.status || 'Pending');
  const [formNotes,    setFormNotes]    = useState(editingOrder?.notes || '');
  const [formItems,    setFormItems]    = useState<FormItem[]>(
    editingOrder?.order_items?.length
      ? editingOrder.order_items.map(i => ({
          item_name: i.item_name || '', quantity: i.quantity || 1,
          unit_price: i.unit_price || 0, size: i.size || '', sku: i.code || '',
        }))
      : [emptyItem()]
  );
  const [busy, setBusy] = useState(false);

  const total = formItems.reduce((s, i) => s + (i.quantity * i.unit_price), 0);

  const updateItem = (idx: number, field: keyof FormItem, value: string | number) => {
    setFormItems(prev => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItemRow = () => setFormItems(prev => [...prev, emptyItem()]);
  const removeItemRow = (idx: number) => {
    if (formItems.length <= 1) return;
    setFormItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!formDept) { showToast('Please select a department'); return; }
    if (!formCategory) { showToast('Please select a category'); return; }
    if (formItems.every(i => !i.item_name.trim())) {
      showToast('Please add at least one item');
      return;
    }
    setBusy(true);

    if (isEdit && editingOrder) {
      const updates: Array<{ field: string; value: string | number }> = [];
      if (formDate     !== (editingOrder.order_date   || '')) updates.push({ field: 'date_ordered', value: formDate });
      if (formStaff    !== (editingOrder.staff_name   || '')) updates.push({ field: 'staff_name',   value: formStaff });
      if (formVendor   !== (editingOrder.vendor       || '')) updates.push({ field: 'vendor_name',  value: formVendor });
      if (formOrderNum !== (editingOrder.order_number || '')) updates.push({ field: 'order_number', value: formOrderNum });
      if (formNotes    !== (editingOrder.notes        || '')) updates.push({ field: 'notes',        value: formNotes });
      if (formCategory !== (editingOrder.category     || '')) updates.push({ field: 'category',     value: formCategory });

      if (updates.length === 0) {
        showToast('No changes to save', 'success');
        setBusy(false);
        onClose();
        return;
      }

      // Server uses optimistic concurrency: every successful update bumps
      // the row's updated_at. Thread the fresh timestamp forward between
      // iterations so later updates aren't rejected as VERSION_CONFLICT
      // by the server we just mutated ourselves.
      let failed = 0;
      let expectedUpdatedAt = editingOrder.updated_at;
      for (const u of updates) {
        const r = await updateOrderField(editingOrder.id, u.field, u.value, expectedUpdatedAt);
        if (!r.ok) { failed++; continue; }
        if (r.data?.updated_at) expectedUpdatedAt = r.data.updated_at;
      }
      if (failed === 0) {
        showToast('Order updated successfully', 'success');
        onSaved();
        onClose();
      } else {
        showToast(`${failed} field(s) failed to update`);
      }
      setBusy(false);
      return;
    }

    // New order
    const validItems = formItems.filter(i => i.item_name.trim());
    const payload = {
      order_date: formDate, date_ordered: formDate, staff_name: formStaff,
      department_id: formDept, department_name: departments.find(d => d.id === formDept)?.name || '',
      vendor: formVendor, vendor_name: formVendor,
      order_number: formOrderNum || undefined,
      status: formStatus as OrderStatus, category: formCategory,
      total_cost: total, notes: formNotes || undefined, source: 'manual',
      order_items: validItems.map(i => ({
        id: '', order_id: '', item_name: i.item_name.trim(), quantity: i.quantity,
        unit_price: i.unit_price || undefined, unit_cost: i.unit_price || undefined,
        size: i.size || undefined, code: i.sku || undefined, sku: i.sku || undefined,
        category: formCategory,
      })),
      items: validItems.map(i => ({
        item_name: i.item_name.trim(), quantity: i.quantity, unit_cost: i.unit_price || 0,
        size: i.size || undefined, sku: i.sku || undefined,
      })),
    };

    const r = await createOrder(payload as any);
    if (r.ok) {
      showToast('Order added successfully', 'success');
      onSaved();
      onClose();
    } else if (r.error === 'DUPLICATE_ORDER') {
      setBusy(false);
      onDuplicate(r.message || 'A similar order already exists.', async () => {
        setBusy(true);
        const force = await createOrder({ ...payload, force: true } as any);
        if (force.ok) { showToast('Order added successfully', 'success'); onSaved(); onClose(); }
        else         { showToast(force.message || force.error || 'Failed to add order'); }
        setBusy(false);
      });
      return;
    } else {
      showToast(r.message || r.error || 'Failed to add order');
    }
    setBusy(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Edit Order' : 'Add New Order'}
      subtitle={
        isEdit
          ? `Editing order ${editingOrder!.order_number || editingOrder!.id.slice(0, 8)}`
          : 'Fill in order details. Items are added below.'
      }
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Order'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', marginBottom: 18 }}>
        <div>
          <label className="field-label">Order Date *</label>
          <input className="input" type="date" value={formDate} onChange={e => setFormDate(e.target.value)} />
        </div>
        <div>
          <label className="field-label">Staff Name</label>
          <input className="input" list="staff-list" value={formStaff} onChange={e => setFormStaff(e.target.value)} placeholder="Type or select…" />
          <datalist id="staff-list">
            {staffDirectory.filter(s => s.active !== false).map(s => <option key={s.id} value={s.name} />)}
          </datalist>
        </div>
        <div>
          <label className="field-label">Department *</label>
          <select
            className="select"
            value={formDept}
            onChange={e => setFormDept(e.target.value)}
            disabled={isEdit}
          >
            <option value="">— Select —</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {isEdit && <div className="field-hint">Department cannot be changed after creation</div>}
        </div>
        <div>
          <label className="field-label">Vendor</label>
          <input className="input" list="vendor-list" value={formVendor} onChange={e => setFormVendor(e.target.value)} placeholder="Type or select…" />
          <datalist id="vendor-list">
            {vendors.filter(v => v.enabled).map(v => <option key={v.id} value={v.name} />)}
          </datalist>
        </div>
        <div>
          <label className="field-label">Category *</label>
          <select className="select" value={formCategory} onChange={e => setFormCategory(e.target.value)}>
            <option value="">— Select —</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Reference Number</label>
          <input className="input" value={formOrderNum} onChange={e => setFormOrderNum(e.target.value)} placeholder="e.g. PO-12345" />
        </div>
        <div>
          <label className="field-label">Status</label>
          <select
            className="select"
            value={formStatus}
            onChange={e => setFormStatus(e.target.value)}
            disabled={isEdit}
          >
            <option value="Pending">Pending</option>
            <option value="Ordered">Ordered</option>
            <option value="Delivered">Delivered</option>
          </select>
          {isEdit && <div className="field-hint">Use the status pill in the table to change status</div>}
        </div>
        <div>
          <label className="field-label">Notes</label>
          <input className="input" value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Optional" />
        </div>
      </div>

      {/* Items */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="field-label" style={{ marginBottom: 0 }}>
            Order Items {isEdit && <span className="font-regular text-subtle">(read-only in edit mode)</span>}
          </label>
          {!isEdit && (
            <button className="btn btn-sm btn-ghost text-accent" onClick={addItemRow} style={{ fontWeight: 600 }}>
              + Add Item
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 80px 100px 28px', gap: 6, marginBottom: 4 }}>
          {['Item Name *', 'Qty', 'Unit Price', 'Size', 'SKU', ''].map((h, i) => (
            <div key={i} className="text-xs font-semibold text-subtle" style={{ textTransform: 'uppercase', fontSize: 9 }}>{h}</div>
          ))}
        </div>
        {formItems.map((item, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 80px 100px 28px', gap: 6, marginBottom: 6 }}>
            <input
              className="input" style={{ fontSize: 12 }}
              value={item.item_name} onChange={e => updateItem(idx, 'item_name', e.target.value)}
              placeholder="Item name" disabled={isEdit}
            />
            <input
              className="input" style={{ fontSize: 12, textAlign: 'center' }}
              type="number" value={item.quantity} min={1}
              onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
              disabled={isEdit}
            />
            <input
              className="input" style={{ fontSize: 12, textAlign: 'right' }}
              type="number" step="0.01" min={0}
              value={item.unit_price || ''} placeholder="0.00"
              onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
              disabled={isEdit}
            />
            <input
              className="input" style={{ fontSize: 12 }}
              value={item.size} onChange={e => updateItem(idx, 'size', e.target.value)}
              placeholder="—" disabled={isEdit}
            />
            <input
              className="input" style={{ fontSize: 12 }}
              value={item.sku} onChange={e => updateItem(idx, 'sku', e.target.value)}
              placeholder="—" disabled={isEdit}
            />
            {!isEdit ? (
              <button
                onClick={() => removeItemRow(idx)}
                disabled={formItems.length <= 1}
                style={{
                  background: 'none', border: 'none',
                  color: formItems.length <= 1 ? 'var(--border)' : 'var(--text3)',
                  cursor: formItems.length <= 1 ? 'default' : 'pointer',
                  fontSize: 14, padding: 0,
                }}
              >×</button>
            ) : <div />}
          </div>
        ))}
        <div className="flex justify-end mt-2" style={{ paddingRight: 36 }}>
          <span className="text-sm font-bold">Total: {formatCurrency(total, currency)}</span>
        </div>
      </div>
    </Modal>
  );
}
